import { FC, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import { DataRoomActivityWindow, DEFAULT_DATA_ROOM_ACTIVITY_WINDOW } from 'shared';
import { twJoin } from 'tailwind-merge';

import { StaggerFadeIn, StaggerItem } from '../../../../components/motion/index';
import { ActivityEmptyState } from '../components/activity-empty-state';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityOverviewPlaceholder } from '../components/activity-loading-placeholders';
import { ActivityMetricCards } from '../components/activity-metric-cards';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { EngagementPanel } from '../components/engagement-chart';
import { MostCards } from '../components/most-cards';
import { useActivityOverview } from '../hooks/use-activity-overview.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

const OverviewContent: FC<ReturnType<typeof useActivityOverview>> = ({
  isError,
  isPending,
  isReloading,
  overview,
  retry,
}) => {
  if (isPending) {
    return <ActivityOverviewPlaceholder />;
  }

  if (isError || !overview) {
    return <ActivityLoadError onRetry={() => void retry()} />;
  }

  // §6: a company with no data room at all has nothing to summarise, which is not the same screen
  // as a company whose rooms have been quiet.
  if (!overview.hasDataRooms) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col items-center">
        <ActivityEmptyState description="NO_ACTIVITY_YET_HINT" title="NO_ACTIVITY_YET" />
      </div>
    );
  }

  return (
    // The sections rise in one after another on arrival; a new window dims them while it loads.
    <StaggerFadeIn
      className={twJoin(
        'flex h-full min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto p-4 transition-opacity duration-200',
        isReloading && 'opacity-60',
      )}
    >
      <StaggerItem>
        <ActivityMetricCards overview={overview} />
      </StaggerItem>
      <StaggerItem>
        <EngagementPanel engagement={overview.engagement} />
      </StaggerItem>
      <StaggerItem>
        <MostCards overview={overview} />
      </StaggerItem>
    </StaggerFadeIn>
  );
};

/**
 * The Overview sub-tab of Data Room Activity — 20.1.1.
 *
 * Who may open it is settled a layer up: the whole module sits behind the Data Rooms access rule,
 * so an Admin, a Contributor and a Guest User with the toggle on reach it and nobody else does.
 */
export const ActivityOverviewTab: FC = () => {
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  // §2: each tab holds its own window, and it starts at 7 days every time the tab is opened.
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const overview = useActivityOverview(window);

  return (
    <>
      {/* Drawn beside the sub-tab buttons, but the window it sets stays this tab's own. */}
      {toolbar &&
        createPortal(<ActivityWindowToggle onChange={setWindow} value={window} />, toolbar)}
      <OverviewContent {...overview} />
    </>
  );
};
